import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  searchAddresses,
  validateAddress,
  getCitiesForPostcode,
  getPostcodesForCity,
} from "../services/addressAutocomplete";
import { getCarpoolLocations } from "../services/carpoolLocationsService";

const AddressAutocomplete = ({
  value = {},
  onChange,
  onValidation,
  placeholder = "Entrez votre adresse",
  showValidation = true,
  className = "",
  showCarpoolButton = true, // Par défaut, afficher le bouton pour les passagers
}) => {
  const [address, setAddress] = useState(value.address || "");
  const [postcode, setPostcode] = useState(value.postcode || "");
  const [city, setCity] = useState(value.city || "");

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({
    isValid: true,
    errors: [],
    warnings: [],
  });

  const [citySuggestions, setCitySuggestions] = useState([]);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);

  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showPostcodeSuggestions, setShowPostcodeSuggestions] = useState(false);
  const [showCarpoolLocationsModal, setShowCarpoolLocationsModal] = useState(false);
  const [carpoolLocations, setCarpoolLocations] = useState([]);

  const addressInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const postcodeInputRef = useRef(null);

  const suggestionTimeoutRef = useRef(null);

  // Recherche d'adresses avec debounce
  const searchAddressesDebounced = useCallback(async (query) => {
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    suggestionTimeoutRef.current = setTimeout(async () => {
      if (query && query.length >= 3) {
        setLoading(true);
        try {
          console.log("🔍 Recherche d'adresses pour:", query);
          const results = await searchAddresses(query, 8);
          console.log("📋 Résultats trouvés:", results);
          console.log(
            "📊 Mise à jour des suggestions:",
            results.length,
            "résultats",
          );
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          console.log("🎯 showSuggestions mis à:", results.length > 0);
        } catch (error) {
          console.error("❌ Erreur recherche adresses:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  }, []);

  // Recherche de villes pour un code postal
  const searchCitiesForPostcode = useCallback(async (code) => {
    if (code && code.length === 5) {
      try {
        const cities = await getCitiesForPostcode(code);
        setCitySuggestions(cities);
        setShowCitySuggestions(cities.length > 0);
      } catch (error) {
        console.error("Erreur recherche villes:", error);
      }
    }
  }, []);

  // Recherche de codes postaux pour une ville
  const searchPostcodesForCity = useCallback(async (cityName) => {
    if (cityName && cityName.length >= 2) {
      try {
        const postcodes = await getPostcodesForCity(cityName);
        setPostcodeSuggestions(postcodes);
        setShowPostcodeSuggestions(postcodes.length > 0);
      } catch (error) {
        console.error("Erreur recherche codes postaux:", error);
      }
    }
  }, []);

  // Gestion des changements d'adresse
  const handleAddressChange = (e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    console.log("📝 Adresse modifiée:", newAddress);
    searchAddressesDebounced(newAddress);

    // Mettre à jour le composant parent
    const newValue = { ...value, address: newAddress };
    onChange?.(newValue);
  };

  // Gestion des changements de code postal
  const handlePostcodeChange = (e) => {
    const newPostcode = e.target.value;
    setPostcode(newPostcode);

    if (newPostcode.length === 5) {
      searchCitiesForPostcode(newPostcode);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }

    const newValue = { ...value, postcode: newPostcode };
    onChange?.(newValue);
  };

  // Gestion des changements de ville
  const handleCityChange = (e) => {
    const newCity = e.target.value;
    setCity(newCity);

    if (newCity.length >= 2) {
      searchPostcodesForCity(newCity);
    } else {
      setPostcodeSuggestions([]);
      setShowPostcodeSuggestions(false);
    }

    const newValue = { ...value, city: newCity };
    onChange?.(newValue);
  };

  // Sélection d'une suggestion d'adresse
  const selectAddressSuggestion = (suggestion) => {
    console.log("🎯 selectAddressSuggestion appelée avec:", suggestion);
    console.log("✅ Suggestion sélectionnée:", suggestion);

    // Si c'est une adresse de covoiturage personnalisée, utiliser le nom au lieu de l'adresse
    let streetAddress;
    if (suggestion.isCarpoolLocation && suggestion.carpoolLocationName) {
      // Pour les adresses de covoiturage, afficher le nom (ex: "Parking Monsabré")
      streetAddress = suggestion.carpoolLocationName;
      console.log("🅿️ Adresse de covoiturage détectée, utilisation du nom:", streetAddress);
    } else {
      // Pour les adresses normales, extraire seulement la partie "rue" de l'adresse
      streetAddress = suggestion.fullAddress
        .replace(suggestion.postcode, '') // Supprimer le code postal
        .replace(suggestion.city, '') // Supprimer la ville
        .replace(/\s+/g, ' ') // Remplacer les espaces multiples par un seul
        .trim(); // Supprimer les espaces en début/fin
      console.log("🏠 Adresse rue extraite:", streetAddress);
    }

    // Mise à jour immédiate des champs locaux
    setAddress(streetAddress);
    setPostcode(suggestion.postcode);
    setCity(suggestion.city);

    // Mise à jour du composant parent
    // Pour les adresses de covoiturage, on envoie le nom dans address mais on garde l'adresse réelle pour le géocodage
    const newValue = {
      address: streetAddress, // Nom pour les adresses de covoiturage, adresse rue pour les autres
      postcode: suggestion.postcode,
      city: suggestion.city,
      // Stocker l'adresse réelle en arrière-plan pour le géocodage si c'est une adresse de covoiturage
      realAddress: suggestion.isCarpoolLocation ? (suggestion.realAddress || suggestion.street) : undefined,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    };

    console.log("📤 Envoi de la valeur:", newValue);
    onChange?.(newValue);

    // Fermer les suggestions
    setShowSuggestions(false);
    setSuggestions([]);

    // Focus sur le code postal
    postcodeInputRef.current?.focus();

    // Log de confirmation
    console.log("✅ Champs mis à jour:", {
      address: streetAddress,
      postcode: suggestion.postcode,
      city: suggestion.city,
      realAddress: newValue.realAddress,
    });
  };

  // Sélection d'une suggestion de ville
  const selectCitySuggestion = (cityName) => {
    setCity(cityName);
    const newValue = { ...value, city: cityName };
    onChange?.(newValue);
    setShowCitySuggestions(false);
    setCitySuggestions([]);

    // Focus sur le code postal
    postcodeInputRef.current?.focus();
  };

  // Sélection d'une suggestion de code postal
  const selectPostcodeSuggestion = (code) => {
    setPostcode(code);
    const newValue = { ...value, postcode: code };
    onChange?.(newValue);
    setShowPostcodeSuggestions(false);
    setPostcodeSuggestions([]);

    // Focus sur la ville
    cityInputRef.current?.focus();
  };

  // Validation de l'adresse complète
  const validateCurrentAddress = useCallback(async () => {
    if (!address || !postcode || !city) {
      return;
    }

    try {
      const result = await validateAddress(address, postcode, city);
      setValidation(result);
      onValidation?.(result);
    } catch (error) {
      console.error("Erreur validation adresse:", error);
    }
  }, [address, postcode, city, onValidation]);

  // Synchronisation avec les changements de value (prop)
  useEffect(() => {
    console.log("🔄 AddressAutocomplete: value prop changée:", value);
    if (value.address !== address) {
      console.log("📝 Mise à jour address locale:", value.address);
      setAddress(value.address || "");
    }
    if (value.postcode !== postcode) {
      console.log("📝 Mise à jour postcode local:", value.postcode);
      setPostcode(value.postcode || "");
    }
    if (value.city !== city) {
      console.log("📝 Mise à jour city locale:", value.city);
      setCity(value.city || "");
    }
  }, [value, address, postcode, city]);

  // Validation lors de la perte de focus
  useEffect(() => {
    if (address && postcode && city) {
      validateCurrentAddress();
    }
  }, [address, postcode, city, validateCurrentAddress]);

  // Fermer les suggestions lors du clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !addressInputRef.current?.contains(event.target) &&
        !cityInputRef.current?.contains(event.target) &&
        !postcodeInputRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
        setShowCitySuggestions(false);
        setShowPostcodeSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Nettoyer le timeout
  useEffect(() => {
    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`address-autocomplete ${className}`}>
      {/* Champ Adresse avec autocomplétion */}
      <div
        className="address-field"
        style={{ position: "relative", marginBottom: "16px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <label
            style={{
              fontWeight: "500",
              color: "#374151",
            }}
          >
            Adresse *
          </label>
          {showCarpoolButton && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const locations = await getCarpoolLocations();
                  setCarpoolLocations(locations);
                  setShowCarpoolLocationsModal(true);
                } catch (error) {
                  console.error("Erreur chargement parkings:", error);
                }
              }}
              style={{
                background: "#f59e0b",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              🅿️ Choisir un parking
            </button>
          )}
        </div>
        <input
          ref={addressInputRef}
          type="text"
          value={address}
          onChange={handleAddressChange}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "12px 16px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "16px",
            transition: "border-color 0.2s",
          }}
          onFocus={() => setShowSuggestions(suggestions.length > 0)}
        />

        {/* Debug info */}
        <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
          🔍 Debug: {suggestions.length} suggestions, loading:{" "}
          {loading ? "oui" : "non"}, showSuggestions:{" "}
          {showSuggestions ? "oui" : "non"}
        </div>

        {/* Suggestions d'adresses */}
        {suggestions.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id || index}
                onClick={() => {
                  console.log("🖱️ Clic détecté sur suggestion:", suggestion);
                  selectAddressSuggestion(suggestion);
                }}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  borderBottom:
                    index < suggestions.length - 1
                      ? "1px solid #f3f4f6"
                      : "none",
                  backgroundColor: suggestion.isCarpoolLocation
                    ? "#fef3c7"
                    : suggestion.isLoirEtCher
                    ? "#f0f9ff"
                    : "white",
                  borderLeft: suggestion.isCarpoolLocation
                    ? "4px solid #f59e0b"
                    : "none",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = suggestion.isCarpoolLocation
                    ? "#fde68a"
                    : "#f3f4f6")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = suggestion.isCarpoolLocation
                    ? "#fef3c7"
                    : suggestion.isLoirEtCher
                    ? "#f0f9ff"
                    : "white")
                }
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  {suggestion.isCarpoolLocation && (
                    <span style={{ fontSize: "20px" }}>🅿️</span>
                  )}
                  <div style={{ fontWeight: "500", flex: 1 }}>
                    {suggestion.isCarpoolLocation && suggestion.carpoolLocationName
                      ? suggestion.carpoolLocationName
                      : suggestion.fullAddress}
                  </div>
                  {suggestion.isCarpoolLocation && (
                    <span
                      style={{
                        backgroundColor: "#f59e0b",
                        color: "white",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      Parking
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280", marginLeft: suggestion.isCarpoolLocation ? "28px" : "0" }}>
                  {suggestion.postcode} {suggestion.city}
                  {suggestion.isLoirEtCher && !suggestion.isCarpoolLocation && (
                    <span
                      style={{
                        marginLeft: "8px",
                        backgroundColor: "#10b981",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      Loir-et-Cher
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "16px",
              color: "#6b7280",
              fontSize: "14px",
              marginTop: "8px",
            }}
          >
            🔍 Recherche en cours...
          </div>
        )}
      </div>

      {/* Champs Code postal et Ville côte à côte */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
        {/* Code postal */}
        <div style={{ flex: 1, position: "relative" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#374151",
            }}
          >
            Code postal *
          </label>
          <input
            ref={postcodeInputRef}
            type="text"
            value={postcode}
            onChange={handlePostcodeChange}
            placeholder="41000"
            maxLength={5}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "16px",
              transition: "border-color 0.2s",
            }}
            onFocus={() =>
              setShowPostcodeSuggestions(postcodeSuggestions.length > 0)
            }
          />

          {/* Suggestions de codes postaux */}
          {showPostcodeSuggestions && postcodeSuggestions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                zIndex: 1000,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {postcodeSuggestions.map((code, index) => (
                <div
                  key={code}
                  onClick={() => selectPostcodeSuggestion(code)}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom:
                      index < postcodeSuggestions.length - 1
                        ? "1px solid #f3f4f6"
                        : "none",
                    backgroundColor: code.startsWith("41")
                      ? "#f0f9ff"
                      : "white",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = code.startsWith("41")
                      ? "#f0f9ff"
                      : "white")
                  }
                >
                  <span style={{ fontWeight: "500" }}>{code}</span>
                  {code.startsWith("41") && (
                    <span
                      style={{
                        marginLeft: "8px",
                        backgroundColor: "#10b981",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      41
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ville */}
        <div style={{ flex: 2, position: "relative" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#374151",
            }}
          >
            Ville *
          </label>
          <input
            ref={cityInputRef}
            type="text"
            value={city}
            onChange={handleCityChange}
            placeholder="Blois"
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "16px",
              transition: "border-color 0.2s",
            }}
            onFocus={() => setShowCitySuggestions(citySuggestions.length > 0)}
          />

          {/* Suggestions de villes */}
          {showCitySuggestions && citySuggestions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                zIndex: 1000,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {citySuggestions.map((cityName, index) => (
                <div
                  key={cityName}
                  onClick={() => selectCitySuggestion(cityName)}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom:
                      index < citySuggestions.length - 1
                        ? "1px solid #f3f4f6"
                        : "none",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f3f4f6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                >
                  {cityName}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Affichage des erreurs et avertissements */}
      {showValidation &&
        (validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div style={{ marginTop: "16px" }}>
            {/* Erreurs */}
            {validation.errors.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                {validation.errors.map((error, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                      borderRadius: "6px",
                      fontSize: "14px",
                      marginBottom: "4px",
                    }}
                  >
                    ❌ {error}
                  </div>
                ))}
              </div>
            )}

            {/* Avertissements */}
            {validation.warnings.length > 0 && (
              <div>
                {validation.warnings.map((warning, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#fffbeb",
                      color: "#d97706",
                      borderRadius: "6px",
                      fontSize: "14px",
                      marginBottom: "4px",
                    }}
                  >
                    ⚠️ {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {/* Indicateur de validation */}
      {showValidation && address && postcode && city && (
        <div
          style={{
            marginTop: "12px",
            padding: "8px 12px",
            backgroundColor: validation.isValid ? "#f0fdf4" : "#fef2f2",
            color: validation.isValid ? "#166534" : "#dc2626",
            borderRadius: "6px",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {validation.isValid ? "✅" : "❌"}
          {validation.isValid ? "Adresse valide" : "Adresse invalide"}
        </div>
      )}

      {/* Modal de sélection des parkings de covoiturage */}
      {showCarpoolLocationsModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "20px",
            paddingTop: "100px", // Descendre un peu pour être visible
            overflowY: "auto",
          }}
          onClick={() => setShowCarpoolLocationsModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "calc(100vh - 140px)", // S'assurer que ça rentre dans l'écran
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>
                🅿️ Choisir un parking de covoiturage
              </h3>
              <button
                onClick={() => setShowCarpoolLocationsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            {carpoolLocations.length === 0 ? (
              <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>
                Aucun parking de covoiturage disponible.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {carpoolLocations.map((location) => (
                  <div
                    key={location.id}
                    onClick={() => {
                      // Pré-remplir avec le parking sélectionné
                      const parkingSuggestion = {
                        id: location.id,
                        fullAddress: location.address 
                          ? `${location.address}, ${location.postalCode} ${location.city}`
                          : `${location.name}, ${location.postalCode} ${location.city}`,
                        street: location.address || location.name,
                        carpoolLocationName: location.name,
                        realAddress: location.address || "",
                        housenumber: "",
                        postcode: location.postalCode,
                        city: location.city,
                        department: "41",
                        isLoirEtCher: true,
                        isCarpoolLocation: true,
                        context: "Parking de covoiturage",
                        score: 1.0,
                        priorityScore: 5000,
                        latitude: location.latitude,
                        longitude: location.longitude,
                      };
                      selectAddressSuggestion(parkingSuggestion);
                      setShowCarpoolLocationsModal(false);
                    }}
                    style={{
                      padding: "16px",
                      border: "2px solid #f59e0b",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: "#fef3c7",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fde68a";
                      e.currentTarget.style.transform = "scale(1.02)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fef3c7";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                      <span style={{ fontSize: "28px" }}>🅿️</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "700", fontSize: "16px", color: "#1f2937" }}>
                          {location.name}
                        </div>
                        {location.address && (
                          <div style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}>
                            {location.address}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280", marginLeft: "40px" }}>
                      {location.postalCode} {location.city}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
